import { Navigate } from "react-router";
import { usePermissions } from "../../hooks/usePermissions";

interface PermissionProtectedRouteProps {
    permission: string;
    children: React.ReactNode;
    redirectTo?: string;
}

/**
 * Componente para proteger rotas baseado em permissões específicas.
 * 
 * @example
 * <Route path="/vendas" element={
 *   <PermissionProtectedRoute permission="sales.view">
 *     <VendasPage />
 *   </PermissionProtectedRoute>
 * } />
 */
export function PermissionProtectedRoute({
    permission,
    children,
    redirectTo
}: PermissionProtectedRouteProps) {
    const { hasPermission, getFirstAvailableRoute, loading } = usePermissions();

    // Durante o carregamento, pode mostrar loading ou nada
    if (loading) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <div className="h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600"></div>
            </div>
        );
    }

    // Se não tiver permissão, redireciona para primeira página disponível
    if (!hasPermission(permission)) {
        const targetRoute = redirectTo || getFirstAvailableRoute();
        return <Navigate to={targetRoute} replace />;
    }

    // Se tiver permissão, renderiza os children
    return <>{children}</>;
}
